import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Trash2, Plus, Check, ChevronLeft, ChevronRight } from 'feather-icons-react/build/IconComponents';
import { saveInspectionParameters, softDeleteInspectionQuestion, softDeleteInspectionResponse } from './store/slice';
import { showToast } from 'Core/redux/toast/slice';

const styles = {
  container: {
    background: 'white',
    borderRadius: '4px',
    padding: '20px',
    maxHeight: '500px',
    overflowY: 'auto',
  },
  addQuestionBtn: {
    backgroundColor: '#ff6b35',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '20px',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'background-color 0.2s',
  },
  questionCard: {
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '15px',
    marginBottom: '15px',
    backgroundColor: '#f9f9f9',
  },
  questionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '15px',
  },
  questionInput: {
    flex: 1,
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    marginRight: '10px',
  },
  deleteBtn: {
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    padding: '8px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    fontSize: '12px',
  },
  responsesSection: {
    marginTop: '10px',
    paddingLeft: '15px',
  },
  responsesTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#666',
    marginBottom: '10px',
  },
  responseRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px',
    gap: '8px',
  },
  responseInput: {
    flex: 1,
    padding: '6px 10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '13px',
  },
  removeResponseBtn: {
    backgroundColor: 'transparent',
    color: '#dc3545',
    border: '1px solid #dc3545',
    padding: '6px 10px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  addResponseBtn: {
    backgroundColor: 'transparent',
    color: '#ff6b35',
    border: '1px solid #ff6b35',
    padding: '6px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    fontSize: '12px',
    marginTop: '5px',
  },
  saveQuestionBtn: {
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    marginTop: '15px',
    transition: 'background-color 0.2s',
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: '#999',
  },
  questionTabs: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px',
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingBottom: '10px',
    flexWrap: 'nowrap', // Prevent wrapping to new line
    // Always show scrollbar
    scrollbarWidth: 'thin', // For Firefox
    scrollbarColor: '#888 #f1f1f1', // For Firefox (thumb track)
  },
  questionTab: {
    padding: '8px 16px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    backgroundColor: '#f9f9f9',
    cursor: 'pointer',
    fontSize: '13px',
    whiteSpace: 'nowrap',
    transition: 'all 0.2s',
  },
  questionTabActive: {
    backgroundColor: '#007bff',
    color: 'white',
    border: '1px solid #007bff',
    fontWeight: '500',
    boxShadow: '0 2px 4px rgba(0,123,255,0.3)',
  },
  navigationButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '20px',
    paddingTop: '15px',
    borderTop: '1px solid #e0e0e0',
  },
  navButton: {
    padding: '8px 16px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    backgroundColor: 'white',
    cursor: 'pointer',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  },
  navButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
};

const createResponseObject = (response, questionId = 'q', index = 0) => {
  let text = '';
  let expectedValue = '';
  let responseId = null;

  if (response && typeof response === 'object') {
    text =
      typeof response.response === 'string'
        ? response.response
        : typeof response.text === 'string'
        ? response.text
        : typeof response.value === 'string'
        ? response.value
        : '';
    expectedValue = response.expectedValue || response.ExpectedValue || response.expectedvalue || response.expected || '';
    responseId = response.id ?? null;
  } else if (typeof response === 'string') {
    text = response;
  }

  const fallbackId = `temp-${questionId}-${index}`;

  return {
    id: responseId !== null && responseId !== undefined ? responseId : fallbackId,
    text,
    expectedValue,
  };
};

const normalizeResponses = (responses, questionId) => {
  if (!Array.isArray(responses) || responses.length === 0) {
    return [createResponseObject('', questionId, 0)];
  }

  const normalized = responses.map((response, index) => createResponseObject(response, questionId, index));

  return normalized.length > 0 ? normalized : [createResponseObject('', questionId, 0)];
};

const createQuestionObject = (question, fallbackId = () => `${Date.now()}`) => {
  if (!question || typeof question !== 'object') {
    const fallback = `temp-${fallbackId()}`;
    return {
      id: fallback,
      question: '',
      responses: normalizeResponses([], fallback),
    };
  }

  const rawFallback = `${fallbackId()}`;
  const resolvedId =
    question.id !== undefined && question.id !== null
      ? question.id
      : `temp-${rawFallback}`;
  const prompt =
    typeof question.question === 'string'
      ? question.question
      : typeof question.name === 'string'
      ? question.name
      : '';

  const responses = normalizeResponses(question.responses, resolvedId);

  return {
    id: resolvedId,
    question: prompt,
    responses,
  };
};

const normalizeQuestions = questions => {
  if (!Array.isArray(questions) || questions.length === 0) {
    return [];
  }

  return questions.map((question, index) => createQuestionObject(question, () => `${Date.now()}-${index}`));
};

const denormalizeQuestions = questions =>
  questions.map(question => ({
    id: typeof question.id === 'number' ? question.id : null,
    question: question.question,
    responses: question.responses.map(response => ({
      id: typeof response.id === 'number' ? response.id : null,
      // Use "text" to align with API validation (Text field required)
      text: response.text ?? '',
      expectedValue: response.expectedValue ?? '',
    })),
  }));

const getPersistedId = id => {
  const numericId = Number(id);
  if (!Number.isFinite(numericId) || numericId <= 0) return null;
  if (String(id).startsWith('temp-')) return null;
  return numericId;
};

const Parameter = ({ data, onChanges, resolvedPartId, activeParameterId, inspectionPlanId }) => {
  // Initialize questions from data or start with empty array
  const [questions, setQuestions] = useState(normalizeQuestions(data));
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0); // Track which question is active
  const dispatch = useDispatch();
  const isPersistedPlan = Boolean(Number(inspectionPlanId) > 0);
  const isInternalUpdateRef = useRef(false);
  const activeQuestionIndexRef = useRef(0); // Track active index with ref to prevent unnecessary updates
  const pendingNewQuestionIndexRef = useRef(null); // Track pending new question index to preserve it
  const lastProcessedActiveParameterIdRef = useRef(null); // Track the last processed activeParameterId to prevent re-triggering
  const recentlySavedQuestionRef = useRef(null); // Track recently saved question {oldId, newId, questionText} to preserve it

  const emitChanges = useCallback(
    updatedQuestions => {
      if (onChanges) {
        isInternalUpdateRef.current = true;
        onChanges(denormalizeQuestions(updatedQuestions));
      }
    },
    [onChanges],
  );

  useEffect(() => {
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      // If we have a pending new question index, preserve it
      if (pendingNewQuestionIndexRef.current !== null) {
        const pendingIndex = pendingNewQuestionIndexRef.current;
        pendingNewQuestionIndexRef.current = null;
        setActiveQuestionIndex(pendingIndex);
        activeQuestionIndexRef.current = pendingIndex;
      }
      return;
    }

    if (!Array.isArray(data)) {
      setQuestions([]);
      setActiveQuestionIndex(0);
      activeQuestionIndexRef.current = 0;
      pendingNewQuestionIndexRef.current = null;
      return;
    }

    const normalized = normalizeQuestions(data);
    setQuestions(prevQuestions => {
      const currentHash = JSON.stringify(denormalizeQuestions(prevQuestions));
      const normalizedHash = JSON.stringify(denormalizeQuestions(normalized));

      if (currentHash === normalizedHash) {
        return prevQuestions; // No change, keep existing state
      }

      // Check if it's just a question text update (same structure, different text)
      const isStructuralChange = prevQuestions.length !== normalized.length || 
        prevQuestions.some((q, idx) => {
          const newQ = normalized[idx];
          return !newQ || q.id !== newQ.id || 
            q.responses.length !== (newQ?.responses?.length || 0);
        });

      // If it's not a structural change, preserve the active index and just update the questions
      // But preserve question text and responses from prevQuestions if normalized has empty text
      if (!isStructuralChange) {
        // Merge normalized with prevQuestions to preserve question text and responses that might be lost
        const merged = normalized.map((newQ, idx) => {
          // First try to find by ID (most reliable)
          const prevQById = prevQuestions.find(q => q.id === newQ.id);
          
          if (prevQById && prevQById.id === newQ.id) {
            // IDs match - safe to preserve both question text and responses
            const preservedQuestion = (!newQ.question || !newQ.question.trim()) 
              ? (prevQById.question || '') 
              : newQ.question;
            
            // When IDs match, ALWAYS use previous responses to ensure correct data
            // The previous responses are the user's current state and should be preserved
            // This prevents mixing responses between different parameters
            const preservedResponses = prevQById.responses && prevQById.responses.length > 0
              ? prevQById.responses
              : (newQ.responses || []);
            
            return { 
              ...newQ, 
              question: preservedQuestion,
              responses: preservedResponses
            };
          }
          
          // If ID doesn't match, don't try to preserve anything - use normalized data as-is
          // This prevents mixing data between different parameters
          return newQ;
        });
        return merged;
      }

      // For structural changes (add/remove), update the active index appropriately
      // Also preserve question text when IDs change (e.g., temp ID to database ID)
      
      // First, merge normalized with prevQuestions to preserve question text when IDs change
      // This is important when temp IDs change to database IDs after saving
      let wasRecentlySavedUsed = false;
      const mergedNormalized = normalized.map((newQ, idx) => {
        // Check if this is a recently saved question that we need to preserve
        if (recentlySavedQuestionRef.current && 
            recentlySavedQuestionRef.current.newId === newQ.id) {
          // This is the question we just saved - preserve its question text and responses
          wasRecentlySavedUsed = true;
          return { 
            ...newQ, 
            question: recentlySavedQuestionRef.current.questionText,
            responses: recentlySavedQuestionRef.current.responses && recentlySavedQuestionRef.current.responses.length > 0
              ? recentlySavedQuestionRef.current.responses
              : newQ.responses
          };
        }
        
        // Try to find matching question by ID first (most reliable)
        const prevQById = prevQuestions.find(q => q.id === newQ.id);
        if (prevQById) {
          // If IDs match, preserve question text and responses
          // Always use previous responses if IDs match to ensure correct data
          const preservedQuestion = (!newQ.question || !newQ.question.trim()) 
            ? (prevQById.question || '') 
            : newQ.question;
          
          // When IDs match, ALWAYS use previous responses to ensure correct data
          // The previous responses are the user's current state and should be preserved
          // This prevents mixing responses between different parameters
          const preservedResponses = prevQById.responses && prevQById.responses.length > 0
            ? prevQById.responses
            : (newQ.responses || []);
          
          return { 
            ...newQ, 
            question: preservedQuestion,
            responses: preservedResponses
          };
        }
        
        // If ID doesn't match, check if this matches a recently saved question by old ID
        if (recentlySavedQuestionRef.current && 
            recentlySavedQuestionRef.current.oldId === newQ.id) {
          // This shouldn't happen if IDs changed, but if it does, use the saved text and responses
          wasRecentlySavedUsed = true;
          return { 
            ...newQ, 
            question: recentlySavedQuestionRef.current.questionText,
            responses: recentlySavedQuestionRef.current.responses && recentlySavedQuestionRef.current.responses.length > 0
              ? recentlySavedQuestionRef.current.responses
              : newQ.responses
          };
        }
        
        // If ID doesn't match, this might be a temp ID that changed to database ID
        // Try to match by position, but only if we're confident it's the same question
        // IMPORTANT: Don't preserve responses when matching by position - only preserve question text
        // This prevents mixing responses between different parameters
        const prevQByPos = prevQuestions[idx];
        if (prevQByPos) {
          // Only preserve question text if:
          // 1. The new question text is empty AND
          // 2. The previous question at this position has text
          // This prevents overwriting correct data with wrong data
          // But DON'T preserve responses - use new responses to avoid mixing data
          if ((!newQ.question || !newQ.question.trim()) && 
              prevQByPos.question && prevQByPos.question.trim()) {
            return { 
              ...newQ, 
              question: prevQByPos.question
              // Don't preserve responses when matching by position - could be wrong parameter
            };
          }
        }
        
        return newQ;
      });
      
      setActiveQuestionIndex(prevIndex => {
        if (mergedNormalized.length === 0) {
          activeQuestionIndexRef.current = 0;
          pendingNewQuestionIndexRef.current = null;
          return 0;
        }
        
        // If we have a pending new question index, prioritize it (for newly added questions)
        // This ensures newly added questions stay selected even after data normalization
        if (pendingNewQuestionIndexRef.current !== null) {
          const pendingIndex = pendingNewQuestionIndexRef.current;
          // Check if the pending index is valid for the normalized array
          if (pendingIndex >= 0 && pendingIndex < mergedNormalized.length) {
            activeQuestionIndexRef.current = pendingIndex;
            // Clear the pending index after using it, but only if it's valid
            pendingNewQuestionIndexRef.current = null;
            return pendingIndex;
          }
          // If pending index is out of bounds, clear it
          pendingNewQuestionIndexRef.current = null;
        }
        
        // Try to find the same question by ID to preserve selection
        const currentQuestion = prevQuestions[prevIndex];
        if (currentQuestion) {
          const foundIndex = mergedNormalized.findIndex(q => q.id === currentQuestion.id);
          if (foundIndex !== -1) {
            activeQuestionIndexRef.current = foundIndex;
            return foundIndex;
          }
        }
        
        // Check if we have a recently saved question - prioritize it
        if (recentlySavedQuestionRef.current) {
          const savedIndex = mergedNormalized.findIndex(q => q.id === recentlySavedQuestionRef.current.newId);
          if (savedIndex !== -1) {
            activeQuestionIndexRef.current = savedIndex;
            return savedIndex;
          }
        }
        
        // If the previous index was the last item and we added a new one, go to the last item
        // This handles the case where we added a new question at the end
        if (prevIndex === prevQuestions.length - 1 && mergedNormalized.length > prevQuestions.length) {
          const lastIndex = mergedNormalized.length - 1;
          activeQuestionIndexRef.current = lastIndex;
          return lastIndex;
        }
        
        // Default: try to maintain position or use last valid index
        const newIndex = Math.min(prevIndex, mergedNormalized.length - 1);
        activeQuestionIndexRef.current = newIndex;
        return newIndex;
      });

      // Clear recentlySavedQuestionRef if it was used in the merge
      if (wasRecentlySavedUsed && recentlySavedQuestionRef.current) {
        // Clear after a short delay to ensure all updates are complete
        setTimeout(() => {
          recentlySavedQuestionRef.current = null;
        }, 100);
      }

      return mergedNormalized;
    });
  }, [data]);

  useEffect(() => {
    // Only run this effect if activeParameterId is explicitly set (from edit/view button)
    // Don't run it when questions change due to typing
    if (!activeParameterId || !questions.length) {
      lastProcessedActiveParameterIdRef.current = null;
      return;
    }
    
    const activeIdStr = String(activeParameterId);
    
    // If activeParameterId changed, clear the last processed ref to allow processing the new one
    if (lastProcessedActiveParameterIdRef.current !== null && 
        lastProcessedActiveParameterIdRef.current !== activeIdStr) {
      lastProcessedActiveParameterIdRef.current = null;
    }
    
    // If we've already processed this activeParameterId, don't run again
    // This prevents re-triggering when questions change due to typing
    if (lastProcessedActiveParameterIdRef.current === activeIdStr) {
      return;
    }
    
    // Don't change active index if we're in the middle of an internal update
    if (isInternalUpdateRef.current) return;
    
    const targetIndex = questions.findIndex(q => {
      // Match by ID (both string and number comparison)
      const qId = String(q.id);
      return qId === activeIdStr || q.id === activeParameterId;
    });
    if (targetIndex !== -1 && targetIndex !== activeQuestionIndexRef.current) {
      setActiveQuestionIndex(targetIndex);
      activeQuestionIndexRef.current = targetIndex;
      // Mark this activeParameterId as processed so we don't re-trigger when questions change
      lastProcessedActiveParameterIdRef.current = activeIdStr;
    }
  }, [activeParameterId, questions]);
  
  // Keep ref in sync with state
  useEffect(() => {
    activeQuestionIndexRef.current = activeQuestionIndex;
  }, [activeQuestionIndex]);

  // Add custom scrollbar styles for webkit browsers
  useEffect(() => {
    const styleId = 'question-tabs-scrollbar-style';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .question-tabs-container::-webkit-scrollbar {
          height: 8px;
        }
        .question-tabs-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        .question-tabs-container::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 4px;
        }
        .question-tabs-container::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `;
      document.head.appendChild(style);
    }
    return () => {
      const styleElement = document.getElementById(styleId);
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, []);

  // Add a new question
  const addQuestion = () => {
    const newQuestion = createQuestionObject({ question: '', responses: [''] }, () => `${Date.now()}`);
    setQuestions(prevQuestions => {
      const updated = [...prevQuestions, newQuestion];
      const newIndex = updated.length - 1;
      // Set the active index immediately
      setActiveQuestionIndex(newIndex);
      activeQuestionIndexRef.current = newIndex;
      // Store the pending index to preserve it when data comes back from parent
      pendingNewQuestionIndexRef.current = newIndex;
      // Mark as internal update to prevent useEffect from interfering
      isInternalUpdateRef.current = true;
      emitChanges(updated);
      return updated;
    });
  };

  // Remove a question
  const removeQuestion = async questionId => {
    const target = questions.find(q => q.id === questionId);
    if (!target) return;

    const removeFromState = () => {
      setQuestions(prevQuestions => {
        const updated = prevQuestions.filter(q => q.id !== questionId);
        if (activeQuestionIndex >= updated.length) {
          setActiveQuestionIndex(updated.length > 0 ? updated.length - 1 : 0);
        }
        emitChanges(updated);
        return updated;
      });
    };

    const persistedId = getPersistedId(target.id);
    if (persistedId) {
      try {
        await dispatch(softDeleteInspectionQuestion(persistedId)).unwrap();
        removeFromState();
      } catch (error) {
        console.error('Failed to delete parameter:', error);
      }
    } else {
      removeFromState();
    }
  };

  // Navigate to previous question
  const goToPreviousQuestion = () => {
    if (activeQuestionIndex > 0) {
      setActiveQuestionIndex(activeQuestionIndex - 1);
    }
  };

  // Navigate to next question
  const goToNextQuestion = () => {
    if (activeQuestionIndex < questions.length - 1) {
      setActiveQuestionIndex(activeQuestionIndex + 1);
    }
  };

  // Update question text
  const updateQuestion = (questionId, value) => {
    setQuestions(prevQuestions => {
      const updated = prevQuestions.map(q =>
        q.id === questionId ? { ...q, question: value } : q,
      );
      // emitChanges will mark as internal update to prevent useEffect from resetting active index
      emitChanges(updated);
      return updated;
    });
  };

  // Add a response to a question
  const addResponse = questionId => {
    setQuestions(prevQuestions => {
      const updated = prevQuestions.map(q =>
        q.id === questionId
          ? {
              ...q,
              responses: [
                ...q.responses,
                createResponseObject('', q.id, q.responses.length),
              ],
            }
          : q,
      );
      emitChanges(updated);
      return updated;
    });
  };

  // Remove a response from a question
  const removeResponse = async (questionId, responseIndex) => {
    const question = questions.find(q => q.id === questionId);

    // Block saving parameters if the plan is not yet created
    if (!isPersistedPlan) {
      dispatch(
        showToast({
          message: 'Please save the plan first, then save parameters.',
          type: 'warning',
        }),
      );
      return;
    }
    if (!question) return;

    const response = question.responses[responseIndex];

    const removeFromState = () => {
      setQuestions(prevQuestions => {
        const updated = prevQuestions.map(q => {
          if (q.id !== questionId) return q;
          const updatedResponses = q.responses.filter((_, index) => index !== responseIndex);
          return {
            ...q,
            responses:
              updatedResponses.length > 0
                ? updatedResponses.map((resp, idx) => createResponseObject(resp, q.id, idx))
                : [createResponseObject('', q.id, 0)],
          };
        });
        emitChanges(updated);
        return updated;
      });
    };

    if (response && typeof response === 'object' && getPersistedId(response.id)) {
      try {
        await dispatch(softDeleteInspectionResponse(getPersistedId(response.id))).unwrap();
        removeFromState();
      } catch (error) {
        console.error('Failed to delete parameter option:', error);
      }
    } else {
      removeFromState();
    }
  };

  // Update a response value
  const updateResponse = (questionId, responseIndex, value) => {
    setQuestions(prevQuestions => {
      const updated = prevQuestions.map(q =>
        q.id === questionId
          ? {
              ...q,
              responses: q.responses.map((r, index) => {
                if (index !== responseIndex) {
                  return r;
                }
                const base = createResponseObject(r, q.id, index);
                return {
                  ...base,
                  text: value,
                };
              }),
            }
          : q,
      );
      emitChanges(updated);
      return updated;
    });
  };

  // Update expected value for a response
  const updateExpectedValue = (questionId, responseIndex, value) => {
    setQuestions(prevQuestions => {
      const updated = prevQuestions.map(q =>
        q.id === questionId
          ? {
              ...q,
              responses: q.responses.map((r, index) => {
                if (index !== responseIndex) {
                  return r;
                }
                const base = createResponseObject(r, q.id, index);
                return {
                  ...base,
                  expectedValue: value,
                };
              }),
            }
          : q,
      );
      emitChanges(updated);
      return updated;
    });
  };

  // Save individual question
  const saveQuestion = async (questionId) => {
    const question = questions.find(q => q.id === questionId);
    
    // Check if question has valid content
    const hasValidResponses = question.responses.some(r => {
      const responseText = typeof r === 'object' ? r.text : r;
      return responseText && responseText.trim();
    });
    
    if (question && question.question.trim() && hasValidResponses) {
      // Filter out empty responses and prepare response objects with text and expectedValue
      const filteredResponses = question.responses
        .filter(r => {
          const responseText = typeof r === 'object' ? r.text : r;
          return responseText && responseText.trim();
        })
        .map(r => {
          const responseObj = typeof r === 'object' ? r : { text: r, expectedValue: '' };
          return {
            text: responseObj.text || responseObj.response || '',
            expectedValue: responseObj.expectedValue || ''
          };
        });
      
      // Prepare payload for API
      // Include the id if it's from database (not the temporary Date.now() id)
      const questionPayload = {
        question: question.question,
        responses: filteredResponses
      };
      
      // Only include id if it's a real database ID (not temporary frontend ID)
      // Database IDs are typically smaller numbers, frontend IDs are large timestamps
      const persistedId = getPersistedId(question.id);
      if (persistedId) {
        questionPayload.id = persistedId;
      }
      
      // Get inspectionPlanMasterId from inspectionPlanId (if null, use 0)
      const inspectionPlanMasterId =
        inspectionPlanId !== null && inspectionPlanId !== undefined ? Number(inspectionPlanId) : 0;
      
      const payload = {
        partId: resolvedPartId || 0,
        inspectionPlanMasterId: inspectionPlanMasterId,
        parameters: [questionPayload]
      };
      
      // Call API to save parameters
      try {
        const result = await dispatch(saveInspectionParameters(payload)).unwrap();

        if (result && result.parameters && result.parameters.length > 0) {
          // Show success toaster
          dispatch(showToast({
            message: 'Parameter saved successfully',
            type: 'success'
          }));

          const normalizedSaved = createQuestionObject(result.parameters[0]);
          setQuestions(prevQuestions => {
            // Find the question being saved to preserve its text and responses
            const questionBeingSaved = prevQuestions.find(q => q.id === questionId);
            const questionTextToPreserve = questionBeingSaved ? questionBeingSaved.question : '';
            const responsesToPreserve = questionBeingSaved ? questionBeingSaved.responses : [];
            
            // ALWAYS preserve the question text and responses from the current question being saved
            // The user's current state is the source of truth, not the API response
            const savedQuestionText = questionTextToPreserve && questionTextToPreserve.trim()
              ? questionTextToPreserve
              : (normalizedSaved.question && normalizedSaved.question.trim() 
                ? normalizedSaved.question 
                : (questionBeingSaved ? questionBeingSaved.question : ''));
            
            // ALWAYS use the current question's responses - they are what the user entered
            // The API response might have different formatting or incorrect data
            const savedResponses = responsesToPreserve && responsesToPreserve.length > 0
              ? responsesToPreserve
              : (normalizedSaved.responses && normalizedSaved.responses.length > 0
                ? normalizedSaved.responses
                : []);
            
            // Store the saved question details in the ref for use when data flows back from parent
            recentlySavedQuestionRef.current = {
              oldId: questionId,
              newId: normalizedSaved.id,
              questionText: savedQuestionText,
              responses: savedResponses
            };
            
            // Update the saved parameter with the new database ID and keep it in the list
            const updated = prevQuestions.map(q => {
              if (q.id !== questionId) return q;
              return {
                ...q,
                id: normalizedSaved.id,
                question: savedQuestionText,
                responses: savedResponses, // Use preserved responses
              };
            });
            
            // Add a new parameter immediately (open new tab)
            const newQuestion = createQuestionObject({ question: '', responses: [''] }, () => `${Date.now()}`);
            const updatedWithNew = [...updated, newQuestion];
            const newIndex = updatedWithNew.length - 1;
            
            // Set the new parameter as active (switch to new tab)
            setActiveQuestionIndex(newIndex);
            activeQuestionIndexRef.current = newIndex;
            pendingNewQuestionIndexRef.current = newIndex;
            
            // Mark as internal update to prevent useEffect from interfering
            isInternalUpdateRef.current = true;
            emitChanges(updatedWithNew);
            
            return updatedWithNew;
          });
        }
      } catch (error) {
        console.error('Failed to save parameter:', error);
        dispatch(showToast({
          message: 'Failed to save parameter. Please try again.',
          type: 'danger'
        }));
      }
    } else {
      console.warn('Please enter both parameter and at least one value before saving.');
    }
  };

  return (
    <div style={styles.container}>
      <button 
        style={styles.addQuestionBtn}
        onClick={addQuestion}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#ff5722'}
        onMouseLeave={(e) => e.target.style.backgroundColor = '#ff6b35'}
      >
        <Plus size={16} />
        Add New Parameter
      </button>

      {questions.length === 0 ? (
        <div style={styles.emptyState}>
          <p>No parameters added yet. Click "Add New Parameter" to get started.</p>
        </div>
      ) : (
        <>
          {/* Question Tabs */}
          <div style={styles.questionTabs} className="question-tabs-container">
            {questions.map((question, index) => (
              <button
                key={question.id}
                style={{
                  ...styles.questionTab,
                  ...(activeQuestionIndex === index ? styles.questionTabActive : {})
                }}
                onClick={() => setActiveQuestionIndex(index)}
              >
                Parameter {index + 1}
                {question.question && question.question.trim() 
                  ? `: ${question.question.length > 20 ? question.question.substring(0, 20) + '...' : question.question}`
                  : ''}
              </button>
            ))}
          </div>

          {/* Display only the active question */}
          {questions[activeQuestionIndex] && (
          <div key={questions[activeQuestionIndex].id} style={styles.questionCard}>
            <div style={styles.questionHeader}>
              <input
                type="text"
                placeholder={`Enter Parameter ${activeQuestionIndex + 1}`}
                value={questions[activeQuestionIndex].question}
                onChange={(e) => updateQuestion(questions[activeQuestionIndex].id, e.target.value)}
                style={styles.questionInput}
              />
              <button
                style={styles.deleteBtn}
                onClick={() => removeQuestion(questions[activeQuestionIndex].id)}
                title="Delete Parameter"
              >
                <Trash2 size={14} />
                Delete
              </button>
            </div>

            <div style={styles.responsesSection}>
              <div style={styles.responsesTitle}>Parameters:</div>
              {questions[activeQuestionIndex].responses.map((response, rIndex) => (
                <div key={response.id ?? `${questions[activeQuestionIndex].id}-${rIndex}`} style={styles.responseRow}>
                  <input
                    type="text"
                    placeholder={`Parameter ${rIndex + 1}`}
                    value={response?.text ?? ''}
                    onChange={e => updateResponse(questions[activeQuestionIndex].id, rIndex, e.target.value)}
                    style={styles.responseInput}
                  />
                  <input
                    type="text"
                    placeholder="Expected Value"
                    value={response?.expectedValue ?? ''}
                    onChange={e => updateExpectedValue(questions[activeQuestionIndex].id, rIndex, e.target.value)}
                    style={styles.responseInput}
                  />
                  {questions[activeQuestionIndex].responses.length > 1 && (
                    <button
                      style={styles.removeResponseBtn}
                      onClick={() => removeResponse(questions[activeQuestionIndex].id, rIndex)}
                      title="Remove Parameter"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button
                style={styles.addResponseBtn}
                onClick={() => addResponse(questions[activeQuestionIndex].id)}
              >
                <Plus size={14} />
                Add Parameter Option
              </button>
      {isPersistedPlan ? (
        <button
          style={{...styles.saveQuestionBtn, display: 'flex', alignItems: 'center', gap: '5px', justifyContent: 'center'}}
          onClick={() => saveQuestion(questions[activeQuestionIndex].id)}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#218838'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#28a745'}
        >
          <Check size={14} />
          Save Parameter
        </button>
      ) : (
        <p className="text-muted" style={{ marginTop: '12px' }}>
          Save Plan first to enable saving parameters.
        </p>
      )}
            </div>
          </div>
          )}

          {/* Navigation Buttons */}
          <div style={styles.navigationButtons}>
            <button
              style={{
                ...styles.navButton,
                ...(activeQuestionIndex === 0 ? styles.navButtonDisabled : {})
              }}
              onClick={goToPreviousQuestion}
              disabled={activeQuestionIndex === 0}
            >
              <ChevronLeft size={16} />
              Previous Parameter
            </button>
            <span style={{ fontSize: '13px', color: '#666', alignSelf: 'center' }}>
              Parameter {activeQuestionIndex + 1} of {questions.length}
            </span>
            <button
              style={{
                ...styles.navButton,
                ...(activeQuestionIndex === questions.length - 1 ? styles.navButtonDisabled : {})
              }}
              onClick={goToNextQuestion}
              disabled={activeQuestionIndex === questions.length - 1}
            >
              Next Parameter
              <ChevronRight size={16} />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

Parameter.defaultProps = {
  onChanges: () => {},
  data: [],
  resolvedPartId: 0,
  activeParameterId: null,
  inspectionPlanId: 0,
};

Parameter.propTypes = {
  onChanges: PropTypes.func,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      question: PropTypes.string,
      responses: PropTypes.arrayOf(
        PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.shape({
            id: PropTypes.number,
            response: PropTypes.string,
            answer: PropTypes.string,
            text: PropTypes.string,
          }),
        ])
      ),
    })
  ),
  resolvedPartId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  activeParameterId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  inspectionPlanId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default Parameter;

